<template>
  <section id="hero" class="hero-section">
    <v-container class="hero-container">
      <v-row align="center" justify="space-between" class="hero-row">
        <!-- Текстовая колонка (Left) -->
        <v-col cols="12" lg="6" class="hero-content">
          <div ref="badge" class="hero-badge">
            <span class="badge-dot"></span>
            <span>Цифровой сервис ресторатора • OZO.direct</span>
          </div>

          <h1 ref="title" class="hero-title">
            Ozo.<span class="text-neon">direct</span> — цифровая экосистема
            вашего заведения
          </h1>

          <p ref="subtitle" class="hero-subtitle">
            Собственное мобильное приложение ресторана на iOS и Android + QR-веб
            меню. Прямая оплата на счет, экономия до 20% на доставке и рост
            выручки на +25% за счет возврата гостей.
          </p>

          <!-- Интерактивный переключатель языков ИИ-перевода (из презентации) -->
          <div ref="langPill" class="hero-lang-box">
            <span class="lang-label">ИИ-перевод меню в 1 клик:</span>
            <div class="lang-chips">
              <button
                v-for="lang in languages"
                :key="lang.code"
                class="lang-chip"
                :class="{ active: currentLang === lang.code }"
                @click="currentLang = lang.code"
              >
                {{ lang.flag }} {{ lang.name }}
              </button>
            </div>
            <div class="lang-preview-text">
              Образец: <em>"{{ currentDishPreview }}"</em>
            </div>
          </div>

          <ul ref="features" class="hero-features">
            <li class="feature-item">
              <i class="ri-checkbox-circle-fill feature-bullet"></i>
              <span
                >Оплата на счет ресторана без посредников и лишних
                комиссий</span
              >
            </li>
            <li class="feature-item">
              <i class="ri-checkbox-circle-fill feature-bullet"></i>
              <span
                >Система лояльности на базе RFM-анализа (LTV и сегменты)</span
              >
            </li>
            <li class="feature-item">
              <i class="ri-checkbox-circle-fill feature-bullet"></i>
              <span
                >Модуль собственной доставки с интеграцией Яндекс и
                курьеров</span
              >
            </li>
            <li class="feature-item">
              <i class="ri-checkbox-circle-fill feature-bullet"></i>
              <span
                >OZO.OFFICE: аналитика выручки, заказов и популярных блюд</span
              >
            </li>
          </ul>

          <div ref="actions" class="hero-actions">
            <v-btn
              x-large
              rounded
              elevation="0"
              class="btn-cta-neon"
              @click="scrollToContact"
            >
              Заказать демонстрацию
            </v-btn>

            <v-btn
              x-large
              rounded
              outlined
              class="btn-whatsapp"
              href="https://api.whatsapp.com/send?phone=77017224666&text=Здравствуйте! Мне интересны подробности возможностей сервиса Ozo.direct."
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="ri-whatsapp-line mr-2 whatsapp-icon"></i>
              Связаться в WhatsApp
            </v-btn>
          </div>
        </v-col>

        <!-- Визуальная колонка (Right: мокапы из презентации + плавающие бейджи метрик) -->
        <v-col cols="12" lg="6" class="hero-visual">
          <div ref="mockupWrapper" class="mockup-wrapper">
            <!-- Плавающие бейджи с цифрами из презентации -->
            <div class="floating-badge badge-revenue">
              <div class="badge-icon icon-green">
                <i class="ri-line-chart-fill"></i>
              </div>
              <div class="badge-text">
                <span class="badge-num">+25%</span>
                <span class="badge-sub">Рост выручки заведения</span>
              </div>
            </div>

            <div class="floating-badge badge-delivery">
              <div class="badge-icon icon-blue">
                <i class="ri-e-bike-2-fill"></i>
              </div>
              <div class="badge-text">
                <span class="badge-num">-20%</span>
                <span class="badge-sub">Экономия на комиссиях</span>
              </div>
            </div>

            <div class="floating-badge badge-platforms">
              <div class="badge-icon icon-purple">
                <i class="ri-smartphone-line"></i>
              </div>
              <div class="badge-text">
                <span class="badge-brand">iOS • Android • QR</span>
                <span class="badge-sub">Единая база гостей</span>
              </div>
            </div>

            <div class="mockup-glow"></div>
            <div class="mockup-image-box">
              <div class="img-skeleton-box mockup-skeleton-box">
                <img
                  src="@/assets/presentation/app-showcase-base.png"
                  alt="Мобильное приложение ресторана OZO.direct"
                  class="mockup-image floating-element"
                  @load="onImageLoad"
                />
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import gsap from "gsap";

export default {
  name: "HeroSection",
  props: {
    playAnimation: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      hasAnimated: false,
      currentLang: "ru",
      languages: [
        {
          code: "ru",
          name: "Русский",
          flag: "🇷🇺",
          dish: "Томлёные говяжьи рёбрышки с печёным картофелем",
        },
        {
          code: "kz",
          name: "Қазақша",
          flag: "🇰🇿",
          dish: "Бұқтырылған сиыр қабырғалары пеште піскен картоппен",
        },
        {
          code: "en",
          name: "English",
          flag: "🇬🇧",
          dish: "Braised beef ribs with roasted rosemary potatoes",
        },
        { code: "zh", name: "中文", flag: "🇨🇳", dish: "慢炖牛小排配烤土豆" },
      ],
    };
  },
  computed: {
    currentDishPreview() {
      const found = this.languages.find((l) => l.code === this.currentLang);
      return found ? found.dish : this.languages[0].dish;
    },
  },
  watch: {
    playAnimation: {
      immediate: true,
      handler(val) {
        if (val && !this.hasAnimated) {
          this.$nextTick(() => {
            this.initHeroAnimations();
          });
        }
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      const img = this.$el.querySelector(".mockup-image");
      if (img && img.complete && img.naturalWidth > 0) {
        img.classList.add("is-loaded");
        if (img.parentElement) img.parentElement.classList.add("is-loaded");
      }
    });

    if (this.playAnimation && !this.hasAnimated) {
      this.initHeroAnimations();
    }

    this.initInteractiveTilt();
  },
  beforeDestroy() {
    if (this.tiltHandler) {
      window.removeEventListener("mousemove", this.tiltHandler);
    }
  },
  methods: {
    onImageLoad(e) {
      if (e && e.target) {
        e.target.classList.add("is-loaded");
        if (e.target.parentElement) {
          e.target.parentElement.classList.add("is-loaded");
        }
      }
    },
    initHeroAnimations() {
      if (this.hasAnimated) return;
      if (!this.$refs.badge || !this.$refs.title) return;
      this.hasAnimated = true;

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.from(this.$refs.badge, { opacity: 0, y: -20, duration: 0.6 })
        .from(this.$refs.title, { opacity: 0, y: 30, duration: 0.8 }, "-=0.3")
        .from(
          this.$refs.subtitle,
          { opacity: 0, y: 20, duration: 0.6 },
          "-=0.4"
        )
        .from(
          this.$refs.langPill,
          { opacity: 0, y: 15, duration: 0.5 },
          "-=0.3"
        )
        .from(
          this.$refs.features.children,
          {
            opacity: 0,
            x: -20,
            stagger: 0.08,
            duration: 0.5,
          },
          "-=0.3"
        )
        .from(this.$refs.actions, { opacity: 0, y: 20, duration: 0.6 }, "-=0.2")
        .from(
          this.$refs.mockupWrapper,
          {
            opacity: 0,
            scale: 0.94,
            y: 40,
            duration: 0.9,
          },
          "-=0.7"
        );
    },
    scrollToContact() {
      const target = document.getElementById("contact");
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      }
    },
    initInteractiveTilt() {
      if (typeof window === "undefined") return;
      if (window.matchMedia("(pointer: coarse)").matches) return;

      const wrapper = this.$refs.mockupWrapper;
      if (!wrapper) return;

      this.tiltHandler = (e) => {
        const rect = wrapper.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const deltaX = (e.clientX - centerX) / (window.innerWidth / 2);
        const deltaY = (e.clientY - centerY) / (window.innerHeight / 2);

        const tiltX = -deltaY * 5;
        const tiltY = deltaX * 6;

        wrapper.style.transform = `perspective(1000px) rotateX(${tiltX.toFixed(
          2
        )}deg) rotateY(${tiltY.toFixed(2)}deg)`;
      };

      window.addEventListener("mousemove", this.tiltHandler, { passive: true });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/_variables.scss";

.hero-section {
  position: relative;
  background: radial-gradient(
      circle at 85% 15%,
      rgba(0, 230, 118, 0.08) 0%,
      transparent 55%
    ),
    radial-gradient(
      circle at 10% 80%,
      rgba(24, 25, 28, 0.04) 0%,
      transparent 50%
    ),
    linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
  padding: 130px 0 90px;
  overflow: hidden;

  @media (max-width: 960px) {
    padding: 95px 0 55px;
  }
}

.hero-container {
  max-width: $container-max-width;
  margin: 0 auto;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border-radius: $radius-full;
  background: $color-neon-green-soft;
  color: #0b7a3a;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 230, 118, 0.25);
  max-width: 100%;

  @media (max-width: 380px) {
    font-size: 11px;
    padding: 6px 12px;
    gap: 6px;
  }

  .badge-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $color-neon-green;
    box-shadow: 0 0 10px $color-neon-green;
    flex-shrink: 0;
  }
}

.hero-title {
  font-family: $font-family-heading;
  font-size: 44px;
  font-weight: 800;
  line-height: 1.18;
  color: $color-text-dark;
  margin-bottom: 18px;
  letter-spacing: -0.02em;

  .text-neon {
    color: #0ebd60;
  }

  @media (max-width: 960px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 26px;
    line-height: 1.25;
  }
}

.hero-subtitle {
  font-size: 16px;
  line-height: 1.6;
  color: $color-text-secondary;
  margin-bottom: 22px;
  max-width: 540px;

  @media (max-width: 480px) {
    font-size: 14px;
  }
}

/* Языковой селектор ИИ перевода */
.hero-lang-box {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: $radius-md;
  padding: 14px 18px;
  margin-bottom: 24px;
  max-width: 540px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);

  @media (max-width: 480px) {
    padding: 12px 14px;
  }

  .lang-label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
  }

  .lang-chips {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    .lang-chip {
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-radius: $radius-full;
      padding: 5px 12px;
      font-size: 12px;
      font-weight: 600;
      color: $color-text-dark;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: #0ebd60;
      }

      &.active {
        background: $color-graphite;
        color: #ffffff;
        border-color: $color-graphite;
      }
    }
  }

  .lang-preview-text {
    margin-top: 8px;
    font-size: 12px;
    color: #0b7a3a;
    font-weight: 500;
    word-break: break-word;
  }
}

.hero-features {
  list-style: none;
  padding: 0;
  margin: 0 0 32px;

  .feature-item {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: $color-text-dark;
    margin-bottom: 10px;

    .feature-bullet {
      color: #0ebd60;
      font-weight: 800;
      margin-right: 10px;
      font-size: 15px;
    }
  }
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;

    .btn-cta-neon,
    .btn-whatsapp {
      width: 100% !important;
      justify-content: center;
    }
  }

  .btn-cta-neon {
    background-color: $color-neon-green !important;
    color: $color-graphite !important;
    font-weight: 700;
    font-size: 15px;
    text-transform: none;
    letter-spacing: 0;
    padding: 0 32px !important;
    height: 52px !important;
    box-shadow: $shadow-neon !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 28px rgba(0, 230, 118, 0.45) !important;
    }
  }

  .btn-whatsapp {
    border-color: #cbd5e1 !important;
    color: $color-text-dark !important;
    font-weight: 600;
    font-size: 15px;
    text-transform: none;
    letter-spacing: 0;
    padding: 0 24px !important;
    height: 52px !important;
    background-color: #ffffff !important;

    &:hover {
      border-color: $color-graphite !important;
      background-color: #f8fafc !important;
    }

    .whatsapp-icon {
      font-size: 22px;
      color: #25d366;
    }
  }
}

.hero-visual {
  display: flex;
  justify-content: center;
  align-items: center;

  .mockup-wrapper {
    position: relative;
    width: 100%;
    max-width: 580px;
    transform-style: preserve-3d;
    transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    will-change: transform;
  }

  .mockup-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 380px;
    height: 380px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(0, 230, 118, 0.28) 0%,
      rgba(255, 255, 255, 0) 70%
    );
    filter: blur(48px);
    z-index: 0;
    pointer-events: none;
  }

  .mockup-image-box {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: center;

    .mockup-image {
      width: 100%;
      height: auto;
      max-height: 560px;
      object-fit: contain;
      filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.15));
    }
  }
}

/* Floating Badges */
.floating-badge {
  position: absolute;
  z-index: 3;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  border-radius: $radius-md;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: floatSlow 6s ease-in-out infinite alternate;
  transform: translateZ(30px);
  transition: box-shadow 0.3s ease, border-color 0.3s ease;

  &:hover {
    box-shadow: 0 16px 36px rgba(0, 230, 118, 0.22);
    border-color: rgba(0, 230, 118, 0.5);
  }

  @media (max-width: 600px) {
    padding: 8px 12px;
    gap: 8px;
  }

  .badge-icon {
    font-size: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 600px) {
      font-size: 18px;
    }

    &.icon-green {
      color: #0b7a3a;
    }
    &.icon-blue {
      color: #0284c7;
    }
    &.icon-purple {
      color: #7c3aed;
    }
  }

  .badge-text {
    display: flex;
    flex-direction: column;

    .badge-num {
      font-family: $font-family-heading;
      font-size: 20px;
      font-weight: 800;
      color: #0b7a3a;
      line-height: 1.1;

      @media (max-width: 600px) {
        font-size: 15px;
      }
    }

    .badge-brand {
      font-family: $font-family-heading;
      font-size: 14px;
      font-weight: 800;
      color: $color-graphite;

      @media (max-width: 600px) {
        font-size: 12px;
      }
    }

    .badge-sub {
      font-size: 11px;
      color: #64748b;
      font-weight: 600;

      @media (max-width: 600px) {
        font-size: 9px;
      }
    }
  }

  &.badge-revenue {
    top: 20px;
    left: -20px;

    @media (max-width: 960px) {
      left: 0;
      top: 0;
    }
  }

  &.badge-delivery {
    bottom: 50px;
    left: -10px;
    animation-delay: -2s;

    .badge-num {
      color: #0284c7;
    }

    @media (max-width: 960px) {
      bottom: 10px;
      left: 0;
    }
  }

  &.badge-platforms {
    top: 40px;
    right: -10px;
    animation-delay: -3.5s;

    @media (max-width: 960px) {
      right: 0;
      top: 50px;
    }
  }
}

.floating-element {
  animation: floatEffect 5s ease-in-out infinite alternate;
}

@keyframes floatEffect {
  0% {
    transform: translateY(0px);
  }
  100% {
    transform: translateY(-12px);
  }
}

@keyframes floatSlow {
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  100% {
    transform: translateY(-8px) rotate(1deg);
  }
}
</style>
